import { Button, Empty, Grid, Table, Tooltip, Row, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  fetchData,
  fixedTableScrollY,
  handleRowDelete,
  Notify,
} from "../../utils/constants";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ConfirmAction, Department } from "../../utils/types";
import DeleteConfirmationModal from "../Modals/DeleteConfirmationModal";
import DepartmentModal from "../Modals/DepartmentModal";
import { useUser } from "../../context/UserContext";

const Departments = () => {
  const { authUser } = useUser();
  const screens = Grid.useBreakpoint();
  const isDesktop = !!screens.md;
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedDepartment, setSelectedDepartment] =
    useState<Department | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [onConfirmDelete, setOnConfirmDelete] = useState<ConfirmAction | null>(
    null,
  );

  const fetchDepartments = useCallback(async () => {
    setLoading(true);
    try {
      await fetchData(
        `${process.env.REACT_APP_API_URL}/departments/allAllDepartments`,
        setDepartments,
      );
    } catch (error) {
      console.error("Failed to fetch departments data:", error);
      Notify("error", "Could not load departments data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDeleteDepartment = useCallback(
    (dept: Department) => {
      setSelectedDepartment(dept);
      setIsDeleteModalOpen(true);
      setOnConfirmDelete(
        () => () =>
          handleRowDelete(
            "Department",
            `${process.env.REACT_APP_API_URL}/departments/deleteDepartment`,
            dept,
            fetchDepartments,
          ),
      );
    },
    [fetchDepartments],
  );

  useEffect(() => {
    if (!authUser) return;
    fetchDepartments();
  }, [authUser, fetchDepartments]);

  const columns = useMemo<ColumnsType<Department>>(
    () => [
      {
        title: "Name",
        dataIndex: "nameDepartment",
        key: "nameDepartment",
      },
      {
        title: "Company",
        dataIndex: "companyName",
        key: "companyName",
      },
      {
        title: "Manager",
        dataIndex: "managerName",
        key: "managerName",
      },
      {
        title: "Parent Department",
        dataIndex: "parentDepartmentName",
        key: "parentDepartmentName",
      },
      {
        title: "Actions",
        key: "actions",
        width: 220,
        fixed: isDesktop ? "right" : undefined,
        align: "center",
        render: (_: unknown, record: Department) => (
          <Row
            wrap={false}
            justify="center"
            align="middle"
            style={{ gap: 8, flexWrap: "nowrap" }}
          >
            <Tooltip title="Edit department">
              <Button
                icon={<EditOutlined />}
                onClick={() => {
                  setSelectedDepartment(record);
                  setIsModalOpen(true);
                }}
              />
            </Tooltip>
            <Tooltip title="Delete department">
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteDepartment(record)}
              />
            </Tooltip>
          </Row>
        ),
      },
    ],
    [handleDeleteDepartment, isDesktop],
  );

  return (
    <div className="section-container">
      <Typography.Title level={4}>Departments</Typography.Title>
      <DepartmentModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        department={selectedDepartment}
        refreshData={fetchDepartments}
      />
      {onConfirmDelete && (
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          setIsOpen={setIsDeleteModalOpen}
          onCancel={() => setIsDeleteModalOpen(false)}
          onConfirm={() => {
            onConfirmDelete();
            setIsDeleteModalOpen(false);
          }}
        />
      )}
      <Button
        title="Add department"
        className="new-company-btn mb-2"
        onClick={() => {
          setSelectedDepartment(null);
          setIsModalOpen(true);
        }}
        type="primary"
        icon={<PlusOutlined />}
      >
        Add department
      </Button>
      <Table
        className="view-table"
        columns={columns}
        dataSource={departments}
        scroll={{ x: true, y: fixedTableScrollY }}
        rowKey={(record) => `${record.id}-${record.nameDepartment}`}
        pagination={false}
        loading={{ spinning: loading, tip: "Loading departments..." }}
        locale={{
          emptyText: <Empty description="No departments found." />,
        }}
      />
    </div>
  );
};

export default Departments;
